import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { CALENDLY_URL } from '../../config';

const VERTICAL_LOGO = process.env.PUBLIC_URL + '/vertical-logo.svg';

function useDarkMode() {
  const [dark, setDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = e => setDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return dark;
}

const SvgHeart = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
  </svg>
);
const SvgZap = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);
const SvgShield = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

function IconBox({ children }) {
  return (
    <div style={{
      width: 56, height: 56, borderRadius: 14,
      background: 'rgba(34,197,94,0.1)',
      border: '1px solid rgba(34,197,94,0.2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      marginBottom: 18, flexShrink: 0,
    }}>
      {children}
    </div>
  );
}

const WHY = [
  { Icon: SvgHeart,  title: 'Built for Home Health', desc: 'Designed specifically for home health agencies — live-in shifts, split shifts, EVV compliance, mileage reimbursement and all.' },
  { Icon: SvgZap,    title: 'No IT Needed',          desc: 'Set up in minutes. No servers, no installs, no complicated onboarding. Most agencies are running payroll on day one.' },
  { Icon: SvgShield, title: 'Secure & Compliant',    desc: 'Your data is encrypted, role-based, and org-scoped. Managers only see their team. Employees only see themselves.' },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dark = useDarkMode();
  const [scrolled, setScrolled]         = useState(false);
  const [mobileOpen, setMobileOpen]     = useState(false);
  const [showCalendly, setShowCalendly] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  const T = dark ? {
    bg:           '#0d1b2a',
    bgCard:       'rgba(255,255,255,0.025)',
    bgCardBorder: 'rgba(34,197,94,0.1)',
    bgNav:        'rgba(10,15,30,0.95)',
    navBorder:    'rgba(34,197,94,0.08)',
    heading:      '#d1fae5',
    text:         '#e2e8f0',
    textMuted:    'rgba(148,163,184,0.75)',
    sectionBorder:'rgba(34,197,94,0.06)',
    navLink:      'rgba(148,163,184,0.85)',
    navLinkHover: '#d1fae5',
    footerText:   'rgba(100,116,139,0.5)',
  } : {
    bg:           '#f8fafc',
    bgCard:       '#ffffff',
    bgCardBorder: '#e2e8f0',
    bgNav:        'rgba(255,255,255,0.97)',
    navBorder:    '#e2e8f0',
    heading:      '#0f172a',
    text:         '#1e293b',
    textMuted:    '#64748b',
    sectionBorder:'#e2e8f0',
    navLink:      '#475569',
    navLinkHover: '#0f172a',
    footerText:   '#94a3b8',
  };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'DM Sans','Segoe UI',sans-serif", color: T.text, overflowX: 'hidden', transition: 'background 0.2s, color 0.2s' }}>
      <style>{`
        * { box-sizing: border-box; }
        .hn { display:flex; justify-content:space-between; align-items:center; padding:16px 40px; position:fixed; top:0; left:0; right:0; z-index:100; backdrop-filter:blur(12px); }
        .hn-links { display:flex; align-items:center; gap:8px; }
        .hn-mob-btn { display:none; background:none; border:none; font-size:24px; cursor:pointer; }
        .hn-mob-menu { display:none; flex-direction:column; gap:10px; padding:16px 24px; }
        .section { padding:88px 24px; }
        .section-label { font-size:14px; font-weight:800; letter-spacing:.14em; text-transform:uppercase; color:#22c55e; text-align:center; margin-bottom:14px; }
        .section-title { font-size:clamp(32px,5.5vw,52px); font-weight:900; text-align:center; letter-spacing:-1.5px; line-height:1.1; max-width:700px; margin:0 auto 18px; }
        .section-sub { font-size:17px; text-align:center; max-width:520px; margin:0 auto 60px; line-height:1.65; }
        .grid-3 { display:grid; grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); gap:24px; max-width:1060px; margin:0 auto; }
        .footer { padding:32px 40px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:16px; }
        @media(max-width:768px){
          .hn { padding:14px 20px; }
          .hn-links { display:none; }
          .hn-mob-btn { display:block; }
          .hn-mob-menu { display:flex; }
          .section { padding:64px 16px; }
          .footer { padding:24px 20px; flex-direction:column; text-align:center; }
        }
      `}</style>

      {/* ── Nav ── */}
      <nav className="hn" style={{ background: T.bgNav, borderBottom: `1px solid ${T.navBorder}`, boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.1)' : 'none' }}>
        <img src={VERTICAL_LOGO} alt="ClockEarnings" onClick={() => navigate('/')}
          style={{ height: 38, cursor: 'pointer' }} />

        <div className="hn-links">
          <button onClick={() => navigate('/')}
            style={{ background: 'none', border: 'none', color: T.navLink, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '8px 14px', borderRadius: 8 }}
            onMouseEnter={e => e.target.style.color = T.navLinkHover}
            onMouseLeave={e => e.target.style.color = T.navLink}>
            Home
          </button>
          <button onClick={() => navigate('/pricing')}
            style={{ background: 'none', border: 'none', color: T.navLink, fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '8px 14px', borderRadius: 8 }}
            onMouseEnter={e => e.target.style.color = T.navLinkHover}
            onMouseLeave={e => e.target.style.color = T.navLink}>
            Pricing
          </button>
          <button onClick={() => setShowCalendly(true)}
            style={{ padding: '8px 16px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, color: '#6366f1', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            📅 Book a Demo
          </button>
          {user ? (
            <button onClick={() => navigate('/')}
              style={{ padding: '9px 18px', background: 'linear-gradient(135deg,#16a34a,#22c55e)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Go to Dashboard
            </button>
          ) : (
            <>
              <button onClick={() => navigate('/login')}
                style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${T.bgCardBorder}`, borderRadius: 8, color: T.navLink, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Sign in / Register
              </button>
              <button onClick={() => navigate('/register')}
                style={{ padding: '9px 18px', background: 'linear-gradient(135deg,#16a34a,#22c55e)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                Start free trial
              </button>
            </>
          )}
        </div>

        <button className="hn-mob-btn" style={{ color: T.navLink }} onClick={() => setMobileOpen(o => !o)}>{mobileOpen ? '✕' : '☰'}</button>
      </nav>

      {mobileOpen && (
        <div className="hn-mob-menu" style={{ background: T.bgNav, borderBottom: `1px solid ${T.navBorder}` }}>
          <button onClick={() => { navigate('/'); setMobileOpen(false); }}
            style={{ background: 'none', border: 'none', color: T.navLink, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left', padding: '8px 0' }}>
            Home
          </button>
          <button onClick={() => { navigate('/pricing'); setMobileOpen(false); }}
            style={{ background: 'none', border: 'none', color: T.navLink, fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left', padding: '8px 0' }}>
            Pricing
          </button>
          <button onClick={() => { setShowCalendly(true); setMobileOpen(false); }}
            style={{ padding: '10px 16px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 8, color: '#6366f1', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            📅 Book a Demo
          </button>
          {user ? (
            <button onClick={() => { navigate('/'); setMobileOpen(false); }}
              style={{ padding: '10px 16px', background: 'linear-gradient(135deg,#16a34a,#22c55e)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
              Go to Dashboard
            </button>
          ) : (
            <>
              <button onClick={() => navigate('/login')}
                style={{ padding: '10px 16px', background: 'transparent', border: `1px solid ${T.bgCardBorder}`, borderRadius: 8, color: T.navLink, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Sign in / Register
              </button>
              <button onClick={() => navigate('/register')}
                style={{ padding: '10px 16px', background: 'linear-gradient(135deg,#16a34a,#22c55e)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                Start free trial
              </button>
            </>
          )}
        </div>
      )}

      {/* ── About Hero ── */}
      <section style={{ paddingTop: 140, background: '#0d1b2a', borderBottom: '1px solid rgba(34,197,94,0.08)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position:'absolute', top:0, left:'30%', width:500, height:400, background:'radial-gradient(ellipse,rgba(34,197,94,0.07) 0%,transparent 70%)', pointerEvents:'none' }} />
        <div style={{ padding: '0 24px 88px' }}>
          <div className="section-label">About Us</div>
          <h1 className="section-title" style={{ color: '#d1fae5' }}>We built the tool we couldn't find</h1>
          <p className="section-sub" style={{ color: 'rgba(148,163,184,0.85)', fontWeight: 500 }}>
            Home health payroll is complicated. Generic tools weren't built for it — they don't understand live-in shifts, split shifts, or the EVV requirements that agencies deal with every week. ClockEarnings was built from scratch to solve exactly that.
          </p>
          <div className="grid-3">
            {WHY.map(w => (
              <div key={w.title} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(34,197,94,0.12)', borderRadius: 18, padding: '32px 28px' }}>
                <IconBox><w.Icon /></IconBox>
                <div style={{ fontSize: 19, fontWeight: 800, color: '#d1fae5', marginBottom: 10 }}>{w.title}</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: 'rgba(148,163,184,0.8)', lineHeight: 1.7 }}>{w.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section" style={{ textAlign: 'center', position: 'relative', borderTop: `1px solid ${T.sectionBorder}` }}>
        {dark && <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:700, height:300, background:'radial-gradient(ellipse,rgba(34,197,94,0.07) 0%,transparent 70%)', pointerEvents:'none' }} />}
        <h2 style={{ fontSize:'clamp(30px,5vw,48px)', fontWeight:900, color: T.heading, letterSpacing:'-1.5px', maxWidth:640, margin:'0 auto 20px' }}>
          Ready to simplify payroll?
        </h2>
        <p style={{ fontSize: 17, fontWeight: 500, color: T.textMuted, maxWidth: 440, margin: '0 auto 36px', lineHeight: 1.65 }}>
          Start your 14-day free trial — no credit card required. You'll be running payroll the same day.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {user ? (
            <button onClick={() => navigate('/')}
              style={{ padding:'15px 32px', background:'linear-gradient(135deg,#16a34a,#22c55e)', border:'none', borderRadius:12, color:'#fff', fontSize:16, fontWeight:800, cursor:'pointer', boxShadow:'0 6px 24px rgba(34,197,94,0.35)' }}>
              Go to Dashboard
            </button>
          ) : (
            <button onClick={() => navigate('/register')}
              style={{ padding:'15px 32px', background:'linear-gradient(135deg,#16a34a,#22c55e)', border:'none', borderRadius:12, color:'#fff', fontSize:16, fontWeight:800, cursor:'pointer', boxShadow:'0 6px 24px rgba(34,197,94,0.35)' }}>
              Get started free
            </button>
          )}
          <button onClick={() => setShowCalendly(true)}
            style={{ padding:'15px 32px', background: dark ? 'rgba(255,255,255,0.04)' : '#f1f5f9', border:`1px solid ${T.bgCardBorder}`, borderRadius:12, color: T.navLink, fontSize:16, fontWeight:600, cursor:'pointer' }}>
            📅 Book a Demo
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer" style={{ borderTop: `1px solid ${T.sectionBorder}` }}>
        <img src={VERTICAL_LOGO} alt="ClockEarnings" style={{ height: 30 }} />
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/')} style={{ background:'none', border:'none', color: T.footerText, fontSize:14, fontWeight:600, cursor:'pointer', padding:0 }}>Home</button>
          <button onClick={() => navigate('/pricing')} style={{ background:'none', border:'none', color: T.footerText, fontSize:14, fontWeight:600, cursor:'pointer', padding:0 }}>Pricing</button>
          <a href="mailto:contact@flurrysystems.com" style={{ color: T.footerText, fontSize:14, fontWeight:600, textDecoration:'none' }}>Contact</a>
          <button onClick={() => navigate('/privacy')} style={{ background:'none', border:'none', color: T.footerText, fontSize:14, fontWeight:600, cursor:'pointer', padding:0 }}>Privacy Policy</button>
        </div>
        <span style={{ fontSize:13, fontWeight:500, color: T.footerText }}>© {new Date().getFullYear()} ClockEarnings by Flurry Systems</span>
      </footer>

      {/* ── Calendly modal ── */}
      {showCalendly && (
        <div onClick={() => setShowCalendly(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:900, height:'90vh', background:'#fff', borderRadius:16, overflow:'hidden', display:'flex', flexDirection:'column', boxShadow:'0 24px 80px rgba(0,0,0,0.5)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'1rem 1.25rem', borderBottom:'1px solid #e5e7eb', background:'#f9fafb' }}>
              <div>
                <p style={{ margin:0, fontWeight:800, fontSize:'1rem', color:'#111827' }}>📅 Book a Demo</p>
                <p style={{ margin:0, fontSize:'0.8rem', color:'#6b7280' }}>Pick a time and we'll walk you through ClockEarnings live.</p>
              </div>
              <button onClick={() => setShowCalendly(false)} style={{ background:'none', border:'none', fontSize:'1.5rem', color:'#9ca3af', cursor:'pointer' }}>✕</button>
            </div>
            <iframe src={`${CALENDLY_URL}?embed_type=Inline&hide_gdpr_banner=1`} title="Book a Demo" style={{ flex:1, border:'none', width:'100%' }} />
          </div>
        </div>
      )}

    </div>
  );
}
